import { DelegatedEvent, delegatedObserver, markup2element } from "../dom/dom";
import { getWeek, getWeekCount, getWeekStart, WeekNumber, WeekNumberSystem } from "../util/util";
import { ControlOptions } from "./control";
import { Cell, SetPicker } from "./set-picker";

export interface Week extends WeekNumber {
	start: number;
	end: number;
}

/**
@property selector Query selector for the top level picker container
@property weekRangeSelector Optional query selector that point to the element that will display the selected week
@property yearControl Optional
@property yearControl.selector Query selector that points to the element that displays the year which weeks can be picked and the controls to change the year
@property yearControl.labelSelector Optional query selector that points to the element that displays the year which weeks can be picked
@property yearControl.labelSelector Optional query selector that points to the element that houses the controls to change the year
 */
export interface WeekPickerOptions extends ControlOptions {
	weekRangeSelector?: string,
	yearControl?: {
		selector: string,
		labelSelector?: string,
		buttonsSelector?: string
	}
}

export const completeWeekValue = (value: Week): Week => {
	if (value) {
		if (value.system === undefined) {
			Object.assign(value, { system: WeekNumberSystem.ISO8601 });
		}
		if (value.start === undefined) {
			//perform this code in case the start (& end) millis have not been given
			let date = getWeekStart(value);
			const startMillis = date.getTime();
			date.setDate(date.getDate() + 7);
			const endMillis = date.getTime();
			return Object.assign(value, { start: startMillis, end: endMillis });
		}
	}
	return value;
}

export class WeekPicker extends SetPicker<Week> {

	private cellContent: Cell[] = [];

	private year: number;
	
	private yearLabelElem: HTMLElement;
	private  weekRangeContainer: HTMLElement;

	public constructor(options: WeekPickerOptions) {
		super(options, 56);
	}
	
	/**@override */
	public getOptions(): WeekPickerOptions {
		return super.getOptions();
	}

	/**
	* gets called by the Control constructor
	* @override
	*/
	protected build(): void {
		super.build();
		
		this.subscribe({
			next: value => {
				if (value) {
					this.setYear(value.year);
				}
				this.updateRangeLabel();
		        console.log('WeekPicker value changed to  ' + JSON.stringify(this.getValue()));
			}
		});

		 
		this.getCellContainer().classList.add('week');
		
		if(this.getOptions().yearControl){
			const yearControlOptions = this.getOptions().yearControl; 
			if(yearControlOptions.labelSelector){
		       this.yearLabelElem =  this.getContainer().querySelector(yearControlOptions.selector + ' ' +yearControlOptions.labelSelector );
			}
			if (yearControlOptions.buttonsSelector) {
				this.addSubscription(
					delegatedObserver(this.getContainer(), 'click', yearControlOptions.selector + ' ' + yearControlOptions.buttonsSelector + ' button.tlmd-button.modify-number')
						.subscribe(evAndTrgt => {
							let delta = parseFloat(evAndTrgt.target.dataset.delta);
							this.setYear(this.year + Math.round(delta));
						}));
			}
		}
		if (this.getOptions().weekRangeSelector) {
		       this.weekRangeContainer =  this.getContainer().querySelector(this.getOptions().weekRangeSelector );
		       this.updateRangeLabel();
			}

		if (this.cellContainer == undefined) {
			this.cellContainer = <HTMLElement>markup2element('<div class="tlmd-cell-container picker"></div>');
			this.getContainer().append(this.cellContainer);
		}

		this.setYear( this.getValue() ? this.getValue().year : new Date().getFullYear());
	}
	
	public setValue(value: Week | Date): WeekPicker {
		let weekValue: Week;
		if (value !== undefined && (<Date>value).toLocaleTimeString) {
			const date = value ? (<Date>value) : new Date();
			weekValue = <Week>getWeek(date);
		} else {
			weekValue = <Week>value;
		}
		super.setValue(completeWeekValue(weekValue));
		return this;
	}

	
	private updateRangeLabel() {
		if (this.weekRangeContainer) {
			this.weekRangeContainer.innerHTML = this.getWeekLabel(this.getValue());
		}
	}
	
	private getWeekLabel( value: WeekNumber){
			let label = '&nbsp;';
			if (value) {
				const date = new Date(getWeekStart(value).getTime());
				label = `W${value.year}-${String(value.week).padStart(2, '0')} : ${date.toLocaleDateString()} - `;
				date.setDate(date.getDate() + 6);
				label += date.toLocaleDateString();
			}
			return label;
	}

	public setYear(year: number): WeekPicker {
		if (this.year !== year) {
			this.year = year;
			if (this.yearLabelElem) {
				this.yearLabelElem.innerHTML = String(year);
			}


			const currentWeek = getWeek(new Date());
			const weekCountPreviousYear = getWeekCount(year - 1);
			const weekCount = getWeekCount(year);

			let january1 = new Date();
			january1.setTime(0);
			january1.setFullYear(year);
			january1.setMonth(0);
			january1.setDate(1);
			january1.setHours(0);
			january1.setMinutes(0);
			january1.setSeconds(0);
			january1.setMilliseconds(0);

			const firstWeek = getWeek(january1);

			let december31 = new Date();
			december31.setTime(0);
			december31.setFullYear(year);
			december31.setMonth(11);
			december31.setDate(31);
			december31.setHours(0);
			december31.setMinutes(0);
			december31.setSeconds(0);
			december31.setMilliseconds(0);

			const lastWeek = getWeek(december31);

			const weekClasses = (w0: WeekNumber, w1: WeekNumber): string[] => w0.year === w1.year && w0.week === w1.week ? <string[]>undefined : ['disabled'];

			let cellIndex = 0;
			let weekNumber: WeekNumber = Object.assign({}, firstWeek);
			if (firstWeek.year < year) {
				weekNumber.week--;
			} else {
				weekNumber.year--;
				weekNumber.week = weekCountPreviousYear - 1;
			}

			this.cellContent.length = 0;

			this.cellContent.push({ index: cellIndex, content: this.createCellMarkup(weekNumber), value: String(getWeekStart(weekNumber).getTime()), classes: weekClasses(weekNumber, firstWeek) });
			weekNumber.week++;
			cellIndex++;
			this.cellContent.push({ index: cellIndex, content: this.createCellMarkup(weekNumber), value: String(getWeekStart(weekNumber).getTime()), classes: weekClasses(weekNumber, firstWeek) });
			weekNumber.year++;
			weekNumber.week = 1;
			cellIndex++;

			for (let i = 0; i < weekCount; i++) {
				const classes = weekNumber.year === currentWeek.year && weekNumber.week === currentWeek.week ? ['current'] : undefined;
				this.cellContent.push({ index: cellIndex, content: this.createCellMarkup(weekNumber), value: String(getWeekStart(weekNumber).getTime()), classes: classes });
				weekNumber.week++;
				cellIndex++;
			}

			weekNumber.year++;
			weekNumber.week = 1;
			while (cellIndex < this.count) {
				this.cellContent.push({ index: cellIndex, content: this.createCellMarkup(weekNumber), value: String(getWeekStart(weekNumber).getTime()), classes: weekClasses(weekNumber, lastWeek) });
				weekNumber.week++;
				cellIndex++;
			}

			//update the content and value of the cells
			this.updateCells(this.getValue() !== undefined ? String(this.getValue().start) : undefined);
		}
		return this;
	}

	/** @override  */
	protected getCell(cellIndex: number): Cell {
		if (cellIndex >= 0 && cellIndex < this.cellContent.length) {
			return this.cellContent[cellIndex];
		}
		return undefined;
	}
	
	/** @override  */
	protected  mapCellClickEventToValue(delegatedEvent: DelegatedEvent ): Week {
		const value = delegatedEvent.target.dataset.value;
		const startMillis = parseInt(value);

		const date = new Date(startMillis);
		const week = getWeek(date);
		
		date.setDate(date.getDate() + 7);
		const endMillis = date.getTime();

		return Object.assign(week,{ start: startMillis, end: endMillis } );
	};
	
	private createCellMarkup(value: WeekNumber){
		return  '<div title="'+this.getWeekLabel(value)+'">'+String(value.week).padStart(2, '0')+'</div>';
	}

}


/*
export class HourPicker extends SetPicker {

	private cellContent: Cell[] = [];

	private value: number;

	public constructor(protected containerSelector: string, private use24HourFormat = true) {
		super(containerSelector, 25);
	}

	protected build(): void {
		super.build();
		//		this.setYear(new Date().getFullYear());
		this.setYear(2019);
	}

	public getValue(): number {
		return this.value;
	}

	public setValue(value: number): HourPicker {
		this.value = value;
		if (value !== undefined) {
			this.setHour(this.value);
		}
		return this;
	}

	public setDay(day: number): HourPicker {
		const weekCountPreviousYear = getWeekCount(year - 1);
		const weekCount = getWeekCount(year);

		let january1 = new Date();
		january1.setTime(0);
		january1.setFullYear(year);
		january1.setMonth(0);
		january1.setDate(1);
		january1.setHours(0);
		january1.setMinutes(0);
		january1.setSeconds(0);
		january1.setMilliseconds(0);

		const firstWeek = getWeek(january1);

		let cellIndex = 0;
		let weekNumber: WeekNumber = Object.assign({}, firstWeek);
		if (firstWeek.year < year) {
			weekNumber.week--;
		} else {
			weekNumber.year--;
			weekNumber.week = weekCountPreviousYear - 1;
		}

		this.cellContent.push({ index: cellIndex, content: String(weekNumber.week).padStart(2, '0'), value: String(getWeekStart(weekNumber).getTime()), classes: ['earlier'] });
		weekNumber.week++;
		cellIndex++;
		this.cellContent.push({ index: cellIndex, content: String(weekNumber.week).padStart(2, '0'), value: String(getWeekStart(weekNumber).getTime()), classes: ['earlier'] });
		weekNumber.year++;
		weekNumber.week = 1;
		cellIndex++;

		for (let i = 0; i < weekCount; i++) {
			const classes = weekNumber.year === currentWeek.year && weekNumber.week === currentWeek.week ? ['current'] : undefined;
			this.cellContent.push({ index: cellIndex, content: String(weekNumber.week).padStart(2, '0'), value: String(getWeekStart(weekNumber).getTime()), classes: classes });
			weekNumber.week++;
			cellIndex++;
		}

		weekNumber.year++;
		weekNumber.week = 1;
		while (cellIndex < this.count) {
			this.cellContent.push({ index: cellIndex, content: String(weekNumber.week).padStart(2, '0'), value: String(getWeekStart(weekNumber).getTime()), classes: ['later'] });
			weekNumber.week++;
			cellIndex++;
		}

		//update the content and value of the cells
		this.updateCells(this.value !== undefined ? String(this.value) : undefined);

		return this;
	}

	protected cellClicked(value: string): void {
		const hour = parseInt(value);
		this.value = hour;
		console.log('cell clicked represents hour ' + JSON.stringify(this.value));
	}

	protected getCell(cellIndex: number): Cell {
		if (cellIndex >= 0 && cellIndex < 24) {
			const hourContent = this.use24HourFormat ? String(weekNumber.week).padStart(2, '0')
			return { index: cellIndex, content: String(weekNumber.week).padStart(2, '0'), value: String(cellIndex), classes: undefined }
			return this.cellContent[cellIndex];
		}
		return undefined;;
	}
	
	private createCellMarkup(hour: number){
		if(this.use24HourFormat){
		  return  '<div>'+String(hour).padStart(2, '0')+'</div>';
		} else {
			
		  return  '<div>'+String(hour%12).padStart(2, '0')+'</div>';
			
		}
	}

}
*/